import {
  DataModelBuilder, SerializationContext, SerializedData,
} from '../../../../dto/data-model.js';
import { AuditLogEntry } from '../../audit-log-entry.js';
import { EventTicketOrder } from '../../../event-ticket-order.js';
import { MapUtils } from '../../../../../utility/map-utils.js';
import {
  cloneData, cloneList, isSameData, isSameList,
} from '../../../../dto/data-model-utils.js';
import { JSONUtils } from '../../../../../utility/json-utils.js';
import { AuditLogEntryType } from '../../audit-log-entry-type.js';

export class OfflineEventTicketOrderModifiedEntry extends AuditLogEntry {
  static readonly TYPE_ID: string = '7051c7a5042d4e50a2f08dbb1edfa939';

  private constructor(
    public readonly before: EventTicketOrder,
    public readonly after: EventTicketOrder,
    /**
     * The unique IDs of the tickets that were canceled.
     */
    public readonly canceledTickets: string[],
    /**
     * The unique IDs of the tickets that were refunded.
     */
    public readonly refundedTickets: string[],
  ) {
    super();
  }

  get $typeId(): string {
    return OfflineEventTicketOrderModifiedEntry.TYPE_ID;
  }

  get type(): AuditLogEntryType {
    return AuditLogEntryType.OfflineEventTicketOrderModified;
  }

  generateQueryData(): SerializedData {
    return {
      businessRef: this.after.businessRef.encode(),
      eventId: this.after.eventId,
      orderId: this.after.uniqueId,
    };
  }

  static create(args: {
    before: EventTicketOrder,
    after: EventTicketOrder,
    canceledTickets: string[],
    refundedTickets: string[],
  }): OfflineEventTicketOrderModifiedEntry {
    return new OfflineEventTicketOrderModifiedEntry(
        args.before,
        args.after,
        args.canceledTickets,
        args.refundedTickets,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): OfflineEventTicketOrderModifiedEntry {
    return this.create({
      before: context.fromData(MapUtils.getRecord(map, 'before')),
      after: context.fromData(MapUtils.getRecord(map, 'after')),
      canceledTickets: MapUtils.getList(map, 'canceledTickets', (x) => JSONUtils.decodeString(x)),
      refundedTickets: MapUtils.getList(map, 'refundedTickets', (x) => JSONUtils.decodeString(x)),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      before: context.toData(this.before),
      after: context.toData(this.after),
      canceledTickets: this.canceledTickets,
      refundedTickets: this.refundedTickets,
    };
  }

  clone(): this {
    return OfflineEventTicketOrderModifiedEntry.create({
      before: cloneData(this.before),
      after: cloneData(this.after),
      canceledTickets: cloneList(this.canceledTickets),
      refundedTickets: cloneList(this.refundedTickets),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.before, this.before) &&
           isSameData(other.after, this.after) &&
           isSameList(other.canceledTickets, this.canceledTickets) &&
           isSameList(other.refundedTickets, this.refundedTickets);
  }
}

export class OfflineEventTicketOrderModifiedEntryBuilder extends DataModelBuilder<OfflineEventTicketOrderModifiedEntry> {
  build(context: SerializationContext, map: SerializedData): OfflineEventTicketOrderModifiedEntry {
    return OfflineEventTicketOrderModifiedEntry.fromMap(context, map);
  }
}
